import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { GlobalWorkerOptions, getDocument } from "pdfjs-dist";
import { answerFromProcedureDocuments, askPdfWithGemini } from "../../lib/tools/get-answers-from-documents";

export default function Lab() {
  const [inputText, setInputText] = useState("");
  const [sopText, setSopText] = useState<string>("");
  const [answer, setAnswer] = useState<string>("");
  const [matches, setMatches] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [pdfAnswer, setPdfAnswer] = useState<string>("");
  const [pdfRefs, setPdfRefs] = useState<Array<{ quote: string; page?: number; section?: string }>>([]);
  const [isRunning, setIsRunning] = useState(false);
  const [selectedRefIndex, setSelectedRefIndex] = useState<number>(0);
  const [pdfReady, setPdfReady] = useState(false);
  const [pageImageUrl, setPageImageUrl] = useState<string>("");
  const [pageImagePage, setPageImagePage] = useState<number>(0);
  const pageImageUrlRef = useRef<string | null>(null);
  const pageImageCacheRef = useRef<Map<string, string>>(new Map());
  const pdfDocRef = useRef<any>(null);

  // Load default SOP from public file (editable by user)
  useEffect(() => {
    let ignore = false;
    fetch("/inputs/prompt.md")
      .then((r) => r.text())
      .then((text) => {
        if (!ignore) setSopText(text);
      })
      .catch(() => {
        // ignore load errors; user can paste SOP manually
      });
    return () => {
      ignore = true;
    };
  }, []);

  const systemInstruction = useMemo(
    () =>
      [
        "CRITICAL: For ANY question related to the SOP/procedure (steps, order, safety, tools, clarifications), you MUST use grounded answers from the SOP text below.",
        sopText,
      ].join("\n\n"),
    [sopText]
  );

  const runLocalTool = useCallback(() => {
    try {
      setError(null);
      const query = inputText.trim();
      if (!query) return;
      const { answer, matches } = answerFromProcedureDocuments(query, systemInstruction);
      setAnswer(answer);
      setMatches(matches);
    } catch (e: any) {
      setError(e?.message || String(e));
    }
  }, [inputText, systemInstruction]);

  const waitForPdfReady = useCallback(async (timeoutMs: number = 5000) => {
    const start = Date.now();
    while (!pdfDocRef.current) {
      if (Date.now() - start > timeoutMs) {
        throw new Error("PDF not loaded yet. Please try again in a moment.");
      }
      await new Promise((r) => setTimeout(r, 100));
    }
  }, []);

  const runPdfTool = useCallback(async () => {
    const query = inputText.trim();
    if (!query) return;
    try {
      setIsRunning(true);
      setError(null);
      await waitForPdfReady();
      const res = await askPdfWithGemini(query, "/inputs/manual.pdf");
      setPdfAnswer(res.answer);
      setPdfRefs(res.references || []);
      setSelectedRefIndex(0);
    } catch (e: any) {
      setError(e?.message || String(e));
    } finally {
      setIsRunning(false);
    }
  }, [inputText, waitForPdfReady]);

  const targetPage = useMemo(() => {
    if (!pdfRefs.length) return 1;
    const ref = pdfRefs[Math.max(0, Math.min(selectedRefIndex, pdfRefs.length - 1))];
    return Number(ref.page && ref.page > 0 ? ref.page : 1);
  }, [pdfRefs, selectedRefIndex]);

  // Load PDF once
  useEffect(() => {
    (async () => {
      try {
        // Serve worker locally to avoid CDN/cross-origin issues
        GlobalWorkerOptions.workerSrc = "/pdf.worker.min.mjs";
        console.log("[pdfjs] Loading PDF... from /inputs/manual.pdf");
        // If you moved the file, update the URL below
        const loadingTask = getDocument({ url: "/inputs/manual.pdf" as any });
        const pdf = await loadingTask.promise;
        console.log("[pdfjs] PDF loaded", pdf?.numPages);
        pdfDocRef.current = pdf;
        setPdfReady(true);
      } catch (e) {
        console.error("[pdfjs] Failed to load PDF", e);
        setPdfReady(false);
      }
    })();
  }, []);

  // Render a single page into an image URL (PNG), with caching
  const renderPageImage = useCallback(async (pageNumber: number, scale: number = 2): Promise<string> => {
    await waitForPdfReady();
    const key = `${pageNumber}@${scale}`;
    const cached = pageImageCacheRef.current.get(key);
    if (cached) return cached;
    const pdf = pdfDocRef.current;
    const page = await pdf.getPage(pageNumber);
    const viewport = page.getViewport({ scale });
    const canvas: HTMLCanvasElement = document.createElement("canvas");
    canvas.width = Math.floor(viewport.width);
    canvas.height = Math.floor(viewport.height);
    const ctx = canvas.getContext("2d", { alpha: false });
    if (!ctx) throw new Error("Canvas 2D not available");
    await page.render({ canvasContext: ctx as any, viewport }).promise;
    const blob: Blob = await new Promise((resolve, reject) => {
      canvas.toBlob((b) => (b ? resolve(b) : reject(new Error("toBlob failed"))), "image/png");
    });
    const url = URL.createObjectURL(blob);
    pageImageCacheRef.current.set(key, url);
    return url;
  }, [waitForPdfReady]);

  // When references change (after tool output), auto-render the first ref page
  useEffect(() => {
    (async () => {
      if (!pdfReady || !pdfRefs.length) return;
      try {
        const pg = targetPage;
        const url = await renderPageImage(pg, 2);
        if (pageImageUrlRef.current) URL.revokeObjectURL(pageImageUrlRef.current);
        pageImageUrlRef.current = url;
        setPageImageUrl(url);
        setPageImagePage(pg);
      } catch (e) {
        console.error("Failed to pre-render page", e);
      }
    })();
  }, [pdfReady, pdfRefs, targetPage, renderPageImage]);

  // Click on reference: re-render that specific page
  const handleRefClick = useCallback(async (i: number) => {
    setSelectedRefIndex(i);
    const pg = Number(pdfRefs[i]?.page || 1);
    try {
      const url = await renderPageImage(pg, 2);
      if (pageImageUrlRef.current) URL.revokeObjectURL(pageImageUrlRef.current);
      pageImageUrlRef.current = url;
      setPageImageUrl(url);
      setPageImagePage(pg);
    } catch (e) {
      console.error("Failed to render page image", e);
    }
  }, [pdfRefs, renderPageImage]);

  return (
    <div style={{ padding: 20, display: "grid", gap: 16 }}>
      <h2>Lab: Test get_answers_from_documents</h2>
      {error && <div style={{ color: "red" }}>Error: {error}</div>}

      <div style={{ display: "grid", gap: 8 }}>
        <label>
          SOP (editable):
          <textarea
            value={sopText}
            onChange={(e) => setSopText(e.target.value)}
            rows={12}
            style={{ width: "100%" }}
          />
        </label>
        <div style={{ fontSize: 12, color: "#555" }}>
          The system instruction sent to Gemini is the fixed directive plus the SOP above.
        </div>
      </div>

      <div style={{ display: "flex", gap: 8 }}>
        <input
          placeholder="Ask a procedure question..."
          value={inputText}
          onChange={(e) => setInputText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              runLocalTool();
            }
          }}
          style={{ flex: 1 }}
        />
        <button onClick={runLocalTool} disabled={!inputText.trim()}>
          Run Tool
        </button>
        <button onClick={runPdfTool} disabled={!inputText.trim() || isRunning || !pdfReady}>
          {isRunning ? "Running…" : pdfReady ? "Run PDF via Gemini" : "Loading PDF…"}
        </button>
      </div>

      <div style={{ border: "1px solid #ddd", padding: 12, borderRadius: 6 }}>
        <h3>Tool Output</h3>
        {answer ? (
          <div style={{ display: "grid", gap: 8 }}>
            <div>
              <strong>Answer:</strong>
              <div style={{ whiteSpace: "pre-wrap" }}>{answer}</div>
            </div>
            <div>
              <strong>Matches:</strong>
              <ul>
                {matches.map((m, i) => (
                  <li key={i} style={{ whiteSpace: "pre-wrap" }}>{m}</li>
                ))}
              </ul>
            </div>
          </div>
        ) : (
          <div>Run the tool to see results.</div>
        )}
      </div>

      <div style={{ border: "1px solid #ddd", padding: 12, borderRadius: 6 }}>
        <h3>PDF Tool Output (Gemini)</h3>
        {pdfAnswer ? (
          <div style={{ display: "grid", gap: 8 }}>
            <div>
              <strong>Answer:</strong>
              <div style={{ whiteSpace: "pre-wrap" }}>{pdfAnswer}</div>
            </div>
            <div>
              <strong>Raw JSON (debug):</strong>
              <pre style={{ background: "#111", color: "#ddd", padding: 12, borderRadius: 6, overflow: "auto" }}>
{JSON.stringify({ answer: pdfAnswer, references: pdfRefs }, null, 2)}
              </pre>
            </div>
            <div>
              <strong>References:</strong>
              {pdfRefs.length ? (
                <div style={{ display: "grid", gap: 8 }}>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                    {pdfRefs.map((r, i) => (
                      <button
                        key={i}
                        onClick={() => handleRefClick(i)}
                        style={{
                          padding: "4px 8px",
                          borderRadius: 4,
                          border: i === selectedRefIndex ? "2px solid #1976d2" : "1px solid #ccc",
                          background: i === selectedRefIndex ? "#e3f2fd" : "#fff",
                          cursor: "pointer",
                        }}
                        title={`Go to page ${r.page ?? "?"}`}
                      >
                        Ref {i + 1}
                      </button>
                    ))}
                  </div>
                  <div style={{ fontSize: 13, color: "#555", whiteSpace: "pre-wrap" }}>
                    {pdfRefs[selectedRefIndex]?.quote}
                    {pdfRefs[selectedRefIndex]?.page !== undefined && ` (page ${pdfRefs[selectedRefIndex]?.page})`}
                    {pdfRefs[selectedRefIndex]?.section ? ` [${pdfRefs[selectedRefIndex]?.section}]` : ""}
                  </div>
                </div>
              ) : (
                <div>No references returned.</div>
              )}
            </div>
            <div style={{ border: "1px solid #ccc", borderRadius: 6, padding: 8 }}>
              {pageImageUrl ? (
                <div>
                  <div style={{ fontSize: 12, color: "#666", marginBottom: 6 }}>Page {pageImagePage}</div>
                  <img src={pageImageUrl} alt={`Page ${pageImagePage}`} style={{ maxWidth: "100%", height: "auto" }} />
                </div>
              ) : (
                <div style={{ color: "#777" }}>Click a reference to render the page.</div>
              )}
            </div>
          </div>
        ) : (
          <div>Run PDF via Gemini to see results.</div>
        )}
      </div>
    </div>
  );
}

